/**
 * printLabel — iframe-based Rx label printing.
 *
 * WHY AN IFRAME: the previous approach mounted the label in the app document
 * and used `@media print { body * { visibility: hidden } }` to hide everything
 * else. `visibility: hidden` keeps elements in layout, so the print engine
 * still paginated the full ~5000px-tall app DOM into 35–65 blank sheets.
 *
 * This module instead renders the label(s) into a hidden <iframe> whose
 * document contains ONLY the label markup + a self-contained stylesheet.
 * The iframe is its own document, so the print job is exactly the labels —
 * one physical label per page, no blank sheets, regardless of what the host
 * app looks like.
 *
 * The label markup here is intentionally plain HTML/CSS (no MUI / emotion):
 * an iframe document can't see the host app's emotion <style> tags, and a
 * dependency-free template guarantees the printed output never drifts with
 * the component library. The on-screen preview still uses the <RxLabel>
 * React component — both consume the same RxLabelSampleData shape.
 */
import { LabelSize } from '../../services/medicationService';
import { RxLabelSampleData, RX_LABEL_SIZES } from './RxLabel';

const FEDERAL_CAUTIONARY_STATEMENT =
  'CAUTION: Federal law prohibits the transfer of this drug to any person other than the patient for whom it was prescribed.';

/** HTML-escape a string for safe interpolation into the label template. */
function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Inner markup for ONE label — a single `.rx-label` block sized to the
 * physical label. Font sizes are in mm so they scale 1:1 with the label and
 * match the proportions of the on-screen <RxLabel> preview.
 */
export function buildLabelMarkup(data: RxLabelSampleData, size: LabelSize): string {
  const cfg = RX_LABEL_SIZES[size];
  const showSchedule = cfg.showCautionaryStatement && !!data.rx.schedule;

  const clinicLine = [data.clinic.address, data.clinic.phone].filter(Boolean).join(' · ');
  const patientLine =
    `<strong>Patient:</strong> ${esc(data.patient.name)}` +
    (data.patient.species ? ` (${esc(data.patient.species)})` : '') +
    (data.patient.owner ? ` &middot; <strong>Owner:</strong> ${esc(data.patient.owner)}` : '');

  const qtySig =
    (data.rx.quantity ? `<strong>Qty:</strong> ${esc(data.rx.quantity)} &middot; ` : '') +
    `<strong>Sig:</strong> ${esc(data.rx.sig)}`;

  const footerParts: string[] = [];
  if (data.vet.name) {
    footerParts.push(
      `<strong>Rx by:</strong> ${esc(data.vet.name)}` +
        (data.vet.license ? ` (${esc(data.vet.license)})` : ''),
    );
  }
  if (data.rx.issued) footerParts.push(`<strong>Issued:</strong> ${esc(data.rx.issued)}`);
  if (data.rx.expires) footerParts.push(`<strong>Expires:</strong> ${esc(data.rx.expires)}`);
  footerParts.push(
    `<strong>Fill:</strong> ${esc(data.rx.refillsRemaining)} of ${esc(data.rx.refillsAuthorized)}`,
  );

  const warnings =
    cfg.showWarnings && data.warnings && data.warnings.length > 0
      ? `<div class="rx-warnings">${data.warnings
          .map((w) => `<div class="rx-warning">&#9888; ${esc(w)}</div>`)
          .join('')}</div>`
      : '';

  const cautionary = showSchedule
    ? `<div class="rx-cautionary">${esc(FEDERAL_CAUTIONARY_STATEMENT)}</div>`
    : '';

  // alt="" so a broken/omitted logo prints nothing rather than alt text; the
  // URL is esc()'d so quotes can't break out of the src attribute.
  const logo = data.clinic.logoUrl
    ? `<img class="rx-logo" src="${esc(data.clinic.logoUrl)}" alt="" />`
    : '';

  return `
    <div class="rx-label">
      <div class="rx-header">
        ${logo}
        <div class="rx-header-text">
          <div class="rx-clinic-name">${esc(data.clinic.name)}</div>
          ${clinicLine ? `<div class="rx-clinic-sub">${esc(clinicLine)}</div>` : ''}
        </div>
      </div>
      <div class="rx-body">
        <div class="rx-patient">${patientLine}</div>
        <div class="rx-drug">${esc(data.rx.drug)}${
          showSchedule ? ` [${esc(data.rx.schedule)}]` : ''
        }</div>
        <div class="rx-sig">${qtySig}</div>
      </div>
      ${warnings}
      ${cautionary}
      <div class="rx-footer">${footerParts.join(' &middot; ')}</div>
    </div>
  `;
}

/**
 * A full standalone HTML document containing every label for a print job.
 * Each label is its own page (`page-break-after`), and `@page` is pinned to
 * the physical label dimensions so the printer feeds exactly one label per
 * page. All labels in one job share a size (the size toggle is per-dialog).
 */
export function buildLabelDocument(opts: {
  size: LabelSize;
  /** Each entry is one label; `copies` prints that label N times. */
  labels: Array<{ data: RxLabelSampleData; copies?: number }>;
}): string {
  const { size, labels } = opts;
  const cfg = RX_LABEL_SIZES[size];

  // Expand copies into a flat list of label blocks.
  const blocks: string[] = [];
  for (const { data, copies = 1 } of labels) {
    const markup = buildLabelMarkup(data, size);
    for (let i = 0; i < Math.max(1, copies); i++) blocks.push(markup);
  }

  // Typography scales with label height so larger labels (legacy data) don't
  // render sparse. Production prints all use the 30252 Address now.
  const big = cfg.heightMm >= 50;
  const fs = {
    clinicName: big ? 3.4 : 2.4,
    clinicSub: big ? 2.3 : 1.7,
    patient: big ? 2.4 : 1.8,
    drug: big ? 2.9 : 2.1,
    sig: big ? 2.4 : 1.8,
    warning: big ? 2.0 : 1.5,
    cautionary: big ? 1.9 : 1.4,
    footer: big ? 2.1 : 1.6,
  };
  // Logo scales with label size; capped so it never crowds the tight 30252 header.
  const logoMaxHmm = big ? 10 : 6;
  const logoMaxWmm = big ? 34 : 20;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Rx Label</title>
<style>
  @page { size: ${cfg.widthMm}mm ${cfg.heightMm}mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  * { box-sizing: border-box; }
  .rx-label {
    width: ${cfg.widthMm}mm;
    height: ${cfg.heightMm}mm;
    padding: 1.5mm;
    background: #fff;
    color: #000;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.15;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 0.4mm;
    page-break-after: always;
    break-after: page;
  }
  .rx-label:last-child { page-break-after: auto; break-after: auto; }
  .rx-header {
    border-bottom: 0.3mm solid #000;
    padding-bottom: 0.3mm;
    display: flex;
    align-items: flex-start;
    gap: 1mm;
  }
  .rx-header-text { min-width: 0; }
  .rx-logo { max-height: ${logoMaxHmm}mm; max-width: ${logoMaxWmm}mm; object-fit: contain; flex: 0 0 auto; }
  .rx-clinic-name { font-size: ${fs.clinicName}mm; font-weight: 700; line-height: 1; }
  .rx-clinic-sub { font-size: ${fs.clinicSub}mm; line-height: 1.1; }
  .rx-patient { font-size: ${fs.patient}mm; line-height: 1.15; }
  .rx-drug { font-size: ${fs.drug}mm; font-weight: 700; line-height: 1.15; }
  .rx-sig { font-size: ${fs.sig}mm; line-height: 1.15; }
  .rx-warnings { }
  .rx-warning { font-size: ${fs.warning}mm; font-style: italic; line-height: 1.15; }
  .rx-cautionary {
    border: 0.25mm solid #000;
    background: #f1f1f1;
    padding: 0.5mm;
    font-size: ${fs.cautionary}mm;
    font-weight: 600;
    line-height: 1.1;
  }
  .rx-footer {
    margin-top: auto;
    border-top: 0.3mm solid #000;
    padding-top: 0.3mm;
    font-size: ${fs.footer}mm;
    line-height: 1.1;
  }
</style>
</head>
<body>
${blocks.join('\n')}
</body>
</html>`;
}

/**
 * Print an HTML document via a transient hidden iframe. Resolves once the
 * print dialog has been dismissed (afterprint) — or after a safety timeout,
 * so the queue never wedges if the user leaves the dialog open.
 */
export function printHtmlDocument(htmlDoc: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';

    let settled = false;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      // Defer removal a tick so the print spooler has fully grabbed the doc.
      setTimeout(() => {
        iframe.remove();
        resolve();
      }, 250);
    };

    let printTriggered = false;
    // Fire the print sequence exactly once — whether triggered by the iframe's
    // `load` event or by the load-independent fallback below.
    const firePrint = () => {
      if (printTriggered) return;
      printTriggered = true;
      const win = iframe.contentWindow;
      if (!win) {
        cleanup();
        return;
      }
      // One frame for layout to settle before snapshotting for print.
      requestAnimationFrame(() => {
        try {
          win.addEventListener('afterprint', cleanup, { once: true });
          win.focus();
          win.print();
        } catch {
          cleanup();
        }
        // Safety net: some browsers/printer drivers never fire afterprint.
        setTimeout(cleanup, 60000);
      });
    };

    iframe.onload = firePrint;
    // Load-independent fallback. The clinic-logo <img> is the first EXTERNAL
    // subresource in this iframe; a URL that TCP-connects but then stalls its
    // body would otherwise gate `onload` (and the whole print promise) until the
    // browser's network timeout. The logo is preloaded into cache before we get
    // here, so `onload` normally fires well within this window; if it doesn't,
    // we print anyway (label sans logo) rather than hang the caller's promise.
    setTimeout(firePrint, 3000);

    // Set srcdoc BEFORE attaching: appending an iframe with no srcdoc first
    // loads about:blank (a spurious `load`), then srcdoc loads (a second
    // `load`) — which would fire print twice. Setting srcdoc first means the
    // single `load` event is the label document.
    iframe.srcdoc = htmlDoc;
    document.body.appendChild(iframe);
  });
}

/**
 * Preload logo image URLs into the browser cache before printing, so the print
 * iframe renders them from cache: its `load` event (which the print trigger
 * waits on) then fires promptly instead of stalling on a cold fetch, and the
 * logo prints present rather than blank. Bounded by `timeoutMs` so a slow or
 * unreachable logo can't hold the job here; printHtmlDocument additionally has a
 * load-independent print fallback, so the trigger can't wedge either. Resolves
 * when every image settles or the timeout hits.
 */
function preloadImages(urls: string[], timeoutMs = 6000): Promise<void> {
  const unique = Array.from(new Set(urls.filter((u): u is string => !!u)));
  if (unique.length === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let remaining = unique.length;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const settleOne = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    setTimeout(finish, timeoutMs);
    for (const url of unique) {
      const img = new Image();
      img.onload = settleOne;
      img.onerror = settleOne;
      img.src = url;
    }
  });
}

/**
 * Convenience: build the document for a print job and send it to the printer.
 * One call → one OS print dialog → one label per page. Any clinic logo is
 * preloaded first so it renders in the print snapshot instead of blank.
 */
export async function printRxLabels(opts: {
  size: LabelSize;
  labels: Array<{ data: RxLabelSampleData; copies?: number }>;
}): Promise<void> {
  if (opts.labels.length === 0) return;
  await preloadImages(
    opts.labels.map((l) => l.data.clinic.logoUrl).filter((u): u is string => !!u),
  );
  return printHtmlDocument(buildLabelDocument(opts));
}
